// Step-up 2FA verification context for the admin panel.
//
// Usage:
//   const { withStepUp } = useStepUp();
//   async function handleDelete(user) {
//     withStepUp((token) => deleteUser.mutate({ userId: user.id, stepUpToken: token }));
//   }
//
// Internally caches the stepup_token for 14 minutes (1 min under the server's
// 15 min TTL). If the cache is valid, the callback runs immediately; if not,
// the StepUpModal opens and the callback fires after successful verification.

import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";
import { StepUpModal } from "@/components/admin/StepUpModal";

type PendingFn = (token: string) => void | Promise<void>;

interface StepUpContextValue {
  withStepUp: (fn: PendingFn, message?: string) => void;
  hasActiveStepUp: boolean;
}

const StepUpContext = createContext<StepUpContextValue | null>(null);

const STEPUP_TTL_MS = 14 * 60 * 1000; // 14 min (1 min buffer under server 15 min)

export function useStepUp(): StepUpContextValue {
  const ctx = useContext(StepUpContext);
  if (!ctx) throw new Error("useStepUp must be used inside StepUpProvider");
  return ctx;
}

export function StepUpProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState(0);
  const [pending, setPending] = useState<{
    fn: PendingFn;
    message?: string;
  } | null>(null);

  const withStepUp = useCallback(
    (fn: PendingFn, message?: string) => {
      const now = Date.now();
      if (token && expiresAt > now) {
        void fn(token);
        return;
      }
      setPending({ fn, message });
    },
    [token, expiresAt],
  );

  async function handleVerified(newToken: string) {
    setToken(newToken);
    setExpiresAt(Date.now() + STEPUP_TTL_MS);
    const p = pending;
    setPending(null);
    if (p) await p.fn(newToken);
  }

  const value: StepUpContextValue = {
    withStepUp,
    hasActiveStepUp: token !== null && expiresAt > Date.now(),
  };

  return (
    <StepUpContext.Provider value={value}>
      {children}
      <StepUpModal
        open={pending !== null}
        message={pending?.message}
        onVerified={handleVerified}
        onClose={() => setPending(null)}
      />
    </StepUpContext.Provider>
  );
}
